import { useState, useRef } from 'react';
import { X, ChevronLeft, ChevronRight, Paperclip } from 'lucide-react';
import type { Issue, Timestamp, ImageAttachment } from '@/types/reportApp';
import { isValidImageFile, processImageFile } from '@/lib/imageUtils';

interface AddIssueModalProps {
  onClose: () => void;
  onAdd: (issue: Omit<Issue, 'id' | 'status'>) => void | Promise<void>;
}

export function AddIssueModal({ onClose, onAdd }: AddIssueModalProps) {
  const [step, setStep] = useState(1);
  const [flag, setFlag] = useState<'red' | 'orange'>('red');
  const [fileNameInput, setFileNameInput] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<string[]>([]);
  const [timestamps, setTimestamps] = useState<Timestamp[]>([{ start: '', end: '' }]);
  const [description, setDescription] = useState('');
  const [attachments, setAttachments] = useState<ImageAttachment[]>([]);
  const [isUploadingAttachments, setIsUploadingAttachments] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const formatTimestamp = (value: string): string => {
    // Remove all non-digit characters
    const digits = value.replace(/\D/g, '');

    // Format as HH:MM:SS:FF (00:00:00:00)
    if (digits.length === 0) return '';
    if (digits.length <= 2) return digits;
    if (digits.length <= 4) return `${digits.slice(0, 2)}:${digits.slice(2)}`;
    if (digits.length <= 6) return `${digits.slice(0, 2)}:${digits.slice(2, 4)}:${digits.slice(4)}`;
    return `${digits.slice(0, 2)}:${digits.slice(2, 4)}:${digits.slice(4, 6)}:${digits.slice(6, 8)}`;
  };

  const handleAddFileName = () => {
    const trimmedName = fileNameInput.trim();
    if (trimmedName && !selectedFiles.includes(trimmedName)) {
      setSelectedFiles(prev => [...prev, trimmedName]);
      setFileNameInput('');
    }
  };

  const handleRemoveFileName = (fileName: string) => {
    setSelectedFiles(prev => prev.filter(name => name !== fileName));
  };

  const handleFileNameKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddFileName();
    }
  };

  const handleTimestampChange = (index: number, field: 'start' | 'end', value: string) => {
    const formatted = formatTimestamp(value);
    setTimestamps(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: formatted };
      return updated;
    });
  };

  const addTimestamp = () => {
    setTimestamps(prev => [...prev, { start: '', end: '' }]);
  };

  const removeTimestamp = (index: number) => {
    setTimestamps(prev => prev.filter((_, i) => i !== index));
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files;
    if (!fileList || fileList.length === 0) return;

    setIsUploadingAttachments(true);

    try {
      const files = Array.from(fileList);
      const validFiles = files.filter(isValidImageFile);

      if (validFiles.length === 0) {
        alert('Please select valid JPEG or PNG images.');
        return;
      }

      const processedAttachments = await Promise.all(
        validFiles.map(file => processImageFile(file))
      );

      setAttachments(prev => [...prev, ...processedAttachments]);
    } catch (error) {
      console.error('Failed to process images:', error);
      alert('Failed to process images. Please try again with smaller files.');
    } finally {
      setIsUploadingAttachments(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemoveAttachment = (attachmentId: string) => {
    setAttachments(prev => prev.filter(att => att.id !== attachmentId));
  };

  const canProceed = () => {
    if (step === 2 && selectedFiles.length === 0) return false;
    // Step 3 (photos) is optional, always can proceed
    // Step 4 (timestamps) is optional, always can proceed
    if (step === 5 && !description.trim()) return false;
    return true;
  };

  const handleSubmit = () => {
    // Filter out empty timestamps
    const validTimestamps = timestamps.filter(ts => ts.start.trim() !== '');

    onAdd({
      fileNames: selectedFiles,
      flag,
      timestamps: validTimestamps,
      description: description.trim(),
      attachments: attachments.length > 0 ? attachments : undefined,
    });

    // Close modal after successful submission
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-6">
      <div className="bg-white border border-gray-200 shadow-lg rounded-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold">
              Add New Issue
            </h2>
            <p className="text-sm text-gray-500 mt-1">Step {step} of 5</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Progress Bar */}
        <div className="px-6 pt-4">
          <div className="w-full h-1.5 bg-white/10 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[#93DFE4] to-[#10B981] transition-all duration-300"
              style={{ width: `${(step / 5) * 100}%` }}
            />
          </div>
        </div>

        {/* Content */}
        <div className="p-6 min-h-[300px]">
          {/* Step 1: Flag */}
          {step === 1 && (
            <div>
              <h3 className="text-lg font-semibold mb-4">Select Flag Color</h3>
              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => setFlag('red')}
                  className={`p-6 rounded-xl border-2 transition-all ${
                    flag === 'red'
                      ? 'border-[#EF4444] bg-[#EF4444]/20'
                      : 'border-gray-300 bg-white/5 hover:bg-white/10'
                  }`}
                >
                  <div className="w-12 h-12 bg-[#EF4444] rounded-full mx-auto mb-3" />
                  <p className="font-semibold">Red Flag</p>
                  <p className="text-sm text-gray-500 mt-1">Must fix</p>
                </button>
                <button
                  onClick={() => setFlag('orange')}
                  className={`p-6 rounded-xl border-2 transition-all ${
                    flag === 'orange'
                      ? 'border-[#F97316] bg-[#F97316]/20'
                      : 'border-gray-300 bg-white/5 hover:bg-white/10'
                  }`}
                >
                  <div className="w-12 h-12 bg-[#F97316] rounded-full mx-auto mb-3" />
                  <p className="font-semibold">Orange Flag</p>
                  <p className="text-sm text-gray-500 mt-1">Needs verification</p>
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Files */}
          {step === 2 && (
            <div>
              <h3 className="text-lg font-semibold mb-2">Add Affected Files</h3>
              <p className="text-sm text-gray-500 mb-4">Enter file names (press Enter or click Add after each file)</p>

              <div className="flex gap-2 mb-4">
                <input
                  type="text"
                  value={fileNameInput}
                  onChange={(e) => setFileNameInput(e.target.value)}
                  onKeyDown={handleFileNameKeyPress}
                  placeholder="Enter file name (e.g., video.mp4)"
                  className="flex-1 px-4 py-3 bg-white/10 border border-gray-300 rounded-lg font-mono focus:border-wppBlue focus:outline-none"
                />
                <button
                  onClick={handleAddFileName}
                  disabled={!fileNameInput.trim()}
                  className="px-6 py-3 bg-wppBlue text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed"
                >
                  Add
                </button>
              </div>

              {selectedFiles.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm text-gray-500 mb-2">Added Files ({selectedFiles.length}):</p>
                  <div className="space-y-2 max-h-[200px] overflow-y-auto pr-2">
                    {selectedFiles.map((fileName, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-3 bg-[#93DFE4]/20 border border-wppBlue rounded-lg"
                      >
                        <p className="font-mono text-sm break-all flex-1">{fileName}</p>
                        <button
                          onClick={() => handleRemoveFileName(fileName)}
                          className="ml-3 text-[#EF4444] hover:bg-[#EF4444]/20 p-2 rounded transition-colors"
                          title="Remove file"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 3: Photo Upload */}
          {step === 3 && (
            <div>
              <h3 className="text-lg font-semibold mb-2">Attach Photos (Optional)</h3>
              <p className="text-sm text-gray-500 mb-4">
                Add photos to support your issue report (JPEG or PNG only)
              </p>

              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/jpeg,image/jpg,image/png"
                onChange={handlePhotoUpload}
                style={{ display: 'none' }}
              />

              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploadingAttachments}
                className="w-full px-4 py-3 bg-[#93DFE4]/20 text-[#93DFE4] rounded-lg hover:bg-[#93DFE4]/30 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed mb-4"
              >
                <Paperclip className="w-4 h-4 inline mr-2" />
                {isUploadingAttachments ? 'Processing Images...' : 'Select Photos to Upload'}
              </button>

              {attachments.length > 0 && (
                <div>
                  <p className="text-sm text-gray-500 mb-2">
                    Attached Photos ({attachments.length}):
                  </p>
                  <div className="grid grid-cols-[repeat(auto-fill,100px)] gap-3">
                    {attachments.map((attachment) => (
                      <div key={attachment.id} className="relative group">
                        <div className="w-[100px] h-[100px] bg-white/10 backdrop-blur border border-gray-300 rounded-lg overflow-hidden hover:border-wppBlue transition-all">
                          <img
                            src={attachment.base64}
                            alt={attachment.filename}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <button
                          onClick={() => handleRemoveAttachment(attachment.id)}
                          className="absolute -top-2 -right-2 w-6 h-6 bg-[#93DFE4] rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-[#B8EDF0] shadow-lg"
                          title="Remove photo"
                        >
                          <X className="w-4 h-4 text-[#00142D]" />
                        </button>
                        <p className="text-[10px] text-gray-500 mt-1 truncate w-[100px] text-center">
                          {attachment.filename}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 4: Timestamps */}
          {step === 4 && (
            <div>
              <h3 className="text-lg font-semibold mb-2">Timestamps (Optional)</h3>
              <p className="text-sm text-gray-500 mb-4">Add timestamps where the issue occurs. End time is optional.</p>
              <div className="space-y-3">
                {timestamps.map((ts, index) => (
                  <div key={index} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Timestamp {index + 1}</span>
                      {timestamps.length > 1 && (
                        <button
                          onClick={() => removeTimestamp(index)}
                          className="text-xs px-2 py-1 hover:bg-[#EF4444]/20 text-[#EF4444] rounded transition-colors"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-xs text-gray-400 mb-1 block">Start Time</label>
                        <input
                          type="text"
                          value={ts.start}
                          onChange={(e) => handleTimestampChange(index, 'start', e.target.value)}
                          placeholder="00:00:00:00"
                          maxLength={11}
                          className="w-full px-4 py-3 bg-white/10 border border-gray-300 rounded-lg font-mono focus:border-wppBlue focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-400 mb-1 block">End Time (Optional)</label>
                        <input
                          type="text"
                          value={ts.end}
                          onChange={(e) => handleTimestampChange(index, 'end', e.target.value)}
                          placeholder="00:00:00:00"
                          maxLength={11}
                          className="w-full px-4 py-3 bg-white/10 border border-gray-300 rounded-lg font-mono focus:border-wppBlue focus:outline-none"
                        />
                      </div>
                    </div>
                  </div>
                ))}
                <button
                  onClick={addTimestamp}
                  className="w-full px-4 py-2 bg-[#93DFE4]/20 text-[#93DFE4] rounded-lg hover:bg-[#93DFE4]/30 transition-colors text-sm font-medium"
                >
                  + Add Another Timestamp
                </button>
              </div>
            </div>
          )}

          {/* Step 5: Description */}
          {step === 5 && (
            <div>
              <h3 className="text-lg font-semibold mb-2">Description</h3>
              <p className="text-sm text-gray-500 mb-4">Describe the issue</p>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe what needs to be fixed..."
                rows={6}
                className="w-full px-4 py-3 bg-white/10 border border-gray-300 rounded-lg focus:border-wppBlue focus:outline-none resize-none"
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200">
          <button
            onClick={() => setStep(s => Math.max(1, s - 1))}
            disabled={step === 1}
            className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white/10 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            Back
          </button>

          {step < 5 ? (
            <button
              onClick={() => setStep(s => s + 1)}
              disabled={!canProceed()}
              className="flex items-center gap-2 px-6 py-2 bg-wppBlue text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed"
            >
              Next
              <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={!canProceed()}
              className="px-6 py-2 bg-wppBlue text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed"
            >
              Add Issue
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
